import { OnboardingReturn } from '../components/OnboardingFlow'
import React, { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Bell, Bot, Calendar, Clock, DollarSign, Users } from 'lucide-react'

import { supabase, signOut } from '../lib/supabase'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { ScheduleConfiguration } from '../components/ScheduleConfiguration'
import { DeliveryConfiguration } from '../components/DeliveryConfiguration'
import { ManagersConfiguration } from '../components/ManagersConfiguration'
import { AllowedAgentsConfig } from '../components/AllowedAgentsConfig'
import { BudgetConfiguration } from '../components/BudgetConfiguration'
import { EscalationConfiguration } from '../components/EscalationConfiguration'
import { ModelUsageNotificationConfiguration } from '../components/ModelUsageNotificationConfiguration'
import { useTeamAccess } from '../contexts/AuthContext'

// Config and Alerts share the existing settings shell and panels, while
// separate routes keep workspace setup and alert rules in distinct menus.

interface TeamConfigProps {
  teamId: string
  view?: 'config' | 'alerts'
}

// Each settings panel continues to enforce its existing write permissions.

type ConfigSection = 'schedule' | 'escalation' | 'delivery' | 'managers' | 'agents' | 'budgets' | 'model-usage'

const VALID_SECTIONS: ConfigSection[] = ['schedule', 'escalation', 'delivery', 'managers', 'agents', 'budgets', 'model-usage']
const ALERT_SECTIONS: ConfigSection[] = ['budgets', 'model-usage', 'escalation']

interface MenuItem {
  id: ConfigSection
  label: string
  subtitle: string
  icon: React.ComponentType<{ className?: string }>
  available: boolean
}

const TeamConfig: React.FC<TeamConfigProps> = ({ teamId, view = 'config' }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSection = searchParams.get('section') as ConfigSection | null
  const [teamName, setTeamName] = useState<string | undefined>(undefined)
  const { role: accessRole, loading: accessLoading } = useTeamAccess(teamId)
  const userRole = accessRole ?? 'member'
  const section = initialSection && VALID_SECTIONS.includes(initialSection) ? initialSection : view === 'alerts' ? 'budgets' : 'schedule'
  const pageLabel = view === 'alerts' ? 'Alerts' : 'Config'
  const [enabledAgentCount, setEnabledAgentCount] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    supabase
      .from('teams')
      .select('name, standup_config')
      .eq('id', teamId)
      .single()
      .then(({ data }) => {
        if (cancelled) return
        setTeamName(data?.name ?? undefined)
        const config = (data?.standup_config as Record<string, unknown> | null) ?? {}
        const list = config.enabled_agent_types
        setEnabledAgentCount(Array.isArray(list) && list.length > 0 ? list.length : 1)
      })
    return () => {
      cancelled = true
    }
  }, [teamId])

  const goToDashboard = () => navigate(`/dashboard/team/${teamId}`)

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const menuItems: MenuItem[] = useMemo(
    () => [
      {
        id: 'schedule',
        label: 'Schedule',
        subtitle: 'Check-in window & digest',
        icon: Clock,
        available: true,
      },
      {
        id: 'escalation',
        label: 'Reminders & follow-up',
        subtitle: 'Standup reminders & manager alerts',
        icon: Bell,
        available: true,
      },
      {
        id: 'delivery',
        label: 'Delivery',
        subtitle: 'Team digest & schedule overrides',
        icon: Calendar,
        available: true,
      },
      {
        id: 'managers',
        label: 'Managers',
        subtitle: 'Team assignments',
        icon: Users,
        available: true,
      },
      {
        id: 'agents',
        label: 'Allow connectors',
        subtitle: enabledAgentCount !== null ? `${enabledAgentCount} of 3 enabled` : '—',
        icon: Bot,
        available: true,
      },
      {
        id: 'budgets',
        label: 'Budgets & spend alerts',
        subtitle: 'Limits & alert thresholds',
        icon: DollarSign,
        available: true,
      },
      {
        id: 'model-usage',
        label: 'Model usage notifications',
        subtitle: 'Usage notifications',
        icon: Bell,
        available: true,
      },
    ],
    [enabledAgentCount]
  )

  const viewMenuItems = menuItems.filter(item => ALERT_SECTIONS.includes(item.id) === (view === 'alerts'))
  if (view === 'alerts') viewMenuItems.sort((a, b) => ALERT_SECTIONS.indexOf(a.id) - ALERT_SECTIONS.indexOf(b.id))
  const visibleMenuItems = accessLoading
    ? []
    : userRole === 'owner' || userRole === 'admin'
      ? viewMenuItems
      : userRole === 'manager'
      ? viewMenuItems.filter((item) => item.id === 'delivery' || item.id === 'budgets')
      : []
  const hasConfigAccess = userRole === 'owner' || userRole === 'admin' || userRole === 'manager'
  const activeSection = visibleMenuItems.some((item) => item.id === section)
    ? section
    : visibleMenuItems[0]?.id ?? menuItems[0].id
  const activeItem = visibleMenuItems.find((item) => item.id === activeSection) ?? visibleMenuItems[0] ?? menuItems[0]

  if (view === 'config' && initialSection && ALERT_SECTIONS.includes(initialSection)) {
    return <Navigate to={`/dashboard/team/${teamId}/alerts?${searchParams.toString()}`} replace />
  }

  return (
    <SidebarProvider>
      <AppSidebar activeSection={view} onNavigate={goToDashboard} teamId={teamId} />
      <SidebarInset className="min-w-0">
        <TopBar teamId={teamId} teamName={teamName} onLogout={handleLogout} sectionLabel={pageLabel} />
        <main className="min-w-0 bg-background p-4 sm:p-6">
          <OnboardingReturn teamId={teamId} />
          {accessLoading ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,17.5rem)_minmax(0,1fr)]" aria-label="Loading settings permissions">
              <div className="h-[420px] animate-pulse border border-border bg-muted/20" />
              <div className="h-[420px] animate-pulse border border-border bg-muted/20" />
            </div>
          ) : !hasConfigAccess ? (
            <div className="border border-border bg-background p-6 text-sm text-muted-foreground" role="alert">
              {pageLabel} settings are available to workspace owners, admins and managers.
            </div>
          ) : <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,17.5rem)_minmax(0,1fr)]">
            {/* No border-radius, per Figma (2026-09-29) -- was rounded-lg. */}
            <div className="border border-border bg-background shadow-sm">
              <div className="border-b border-border p-4">
                <span className="text-sm font-bold uppercase tracking-wide text-foreground">
                  Menu ({visibleMenuItems.length})
                </span>
              </div>
              <div className="divide-y divide-border">
                {visibleMenuItems.map((item) => {
                  const Icon = item.icon
                  const isActive = item.id === activeSection
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSearchParams(previous => { const next = new URLSearchParams(previous); next.set('section', item.id); return next })}
                      className={`flex min-h-[44px] w-full items-start gap-3 p-4 text-left transition-colors ${
                        isActive ? 'bg-muted' : 'hover:bg-muted/50'
                      }`}
                    >
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div>
                        <div className="text-sm font-medium text-foreground">{item.label}</div>
                        <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* No border-radius, per Figma (2026-09-29) -- was rounded-lg. */}
            <div className="min-w-0 overflow-hidden border border-border bg-background p-0 shadow-sm">
              {activeItem.available ? (
                activeSection === 'schedule' ? (
                  <ScheduleConfiguration teamId={teamId} userRole={userRole} />
                ) : activeSection === 'escalation' ? (
                  <EscalationConfiguration teamId={teamId} userRole={userRole} />
                ) : activeSection === 'delivery' ? (
                  <DeliveryConfiguration teamId={teamId} userRole={userRole} />
                ) : activeSection === 'managers' ? (
                  <ManagersConfiguration teamId={teamId} userRole={userRole} />
                ) : activeSection === 'budgets' ? (
                  <BudgetConfiguration teamId={teamId} userRole={userRole} />
                ) : activeSection === 'model-usage' ? (
                  <ModelUsageNotificationConfiguration teamId={teamId} userRole={userRole} />
                ) : (
                  <AllowedAgentsConfig teamId={teamId} isAdmin={userRole === 'owner' || userRole === 'admin'} />
                )
              ) : (
                <div className="flex flex-col items-center gap-2 py-16 text-center">
                  <activeItem.icon className="h-8 w-8 text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">{activeItem.label}</h2>
                  <p className="text-sm text-muted-foreground">
                    This section isn&apos;t built yet — coming in a future pass.
                  </p>
                </div>
              )}
            </div>
          </div>}
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default TeamConfig
